import { useEffect, useMemo, useState, useCallback } from 'react'
import { funnelApi } from '../api'
import type { Funnel, FunnelFilters, ViewMode } from '../types'
import { DEFAULT_FILTERS } from '../types'

type FunnelWithOptionalFilters = Funnel & {
  currency?: string
  productType?: string
}

/**
 * Filters and sorts funnels based on provided filters
 */
function useFunnelFiltering(funnels: Funnel[], filters: FunnelFilters) {
  return useMemo(() => {
    const query = filters.search.toLowerCase()
    const result = funnels.filter((funnel) => {
      const extendedFunnel = funnel as FunnelWithOptionalFilters

      if (query && !funnel.name.toLowerCase().includes(query) &&
          !funnel.description?.toLowerCase().includes(query) &&
          !funnel.id.toLowerCase().includes(query)) {
        return false
      }
      if (filters.status.length > 0) {
        const matchesStatus = filters.status.some((s) => {
          if (s === 'active') return funnel.isActive
          if (s === 'inactive') return !funnel.isActive
          if (s === 'draft') return !funnel.isActive
          return false
        })
        if (!matchesStatus) return false
      }
      if (filters.funnelType.length > 0 && (!funnel.funnelType || !filters.funnelType.includes(funnel.funnelType))) return false
      if (filters.trafficType.length > 0 && (!funnel.trafficType || !filters.trafficType.includes(funnel.trafficType))) return false
      if (filters.currency.length > 0 && extendedFunnel.currency && !filters.currency.includes(extendedFunnel.currency)) return false
      if (filters.productType.length > 0 && extendedFunnel.productType && !filters.productType.includes(extendedFunnel.productType)) return false
      if (filters.countries.length > 0) {
        const funnelCountries = funnel.countries || []
        if (!filters.countries.some((c) => funnelCountries.includes(c))) return false
      }
      if (filters.dateFrom) {
        const from = new Date(`${filters.dateFrom}T00:00:00.000Z`)
        if (new Date(funnel.createdAt) < from) return false
      }
      if (filters.dateTo) {
        const to = new Date(`${filters.dateTo}T23:59:59.999Z`)
        if (new Date(funnel.createdAt) > to) return false
      }
      return true
    })

    result.sort((a, b) => {
      const field = filters.sortField
      const dir = filters.sortDirection === 'asc' ? 1 : -1
      if (field === 'name') return a.name.localeCompare(b.name) * dir
      if (field === 'priority') return (a.priority - b.priority) * dir
      if (field === 'version') return ((a.version || 0) - (b.version || 0)) * dir
      const aDate = field === 'createdAt' ? a.createdAt : a.updatedAt
      const bDate = field === 'createdAt' ? b.createdAt : b.updatedAt
      return aDate.localeCompare(bDate) * dir
    })

    return result
  }, [funnels, filters])
}

/**
 * Funnel CRUD operations (create, delete, duplicate)
 */
function useFunnelMutations(fetchFunnels: () => Promise<void>) {
  const handleCreate = useCallback(async (data: {
    name: string
    description?: string
    priority?: number
    isActive?: boolean
  }) => {
    await funnelApi.create(data)
    await fetchFunnels()
  }, [fetchFunnels])

  const handleDelete = useCallback(async (id: string) => {
    await funnelApi.delete(id)
    await fetchFunnels()
  }, [fetchFunnels])

  const handleDuplicate = useCallback(async (id: string) => {
    await funnelApi.duplicate(id)
    await fetchFunnels()
  }, [fetchFunnels])

  return { handleCreate, handleDelete, handleDuplicate }
}

export function useFunnels() {
  const [funnels, setFunnels] = useState<Funnel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FunnelFilters>(DEFAULT_FILTERS)
  const [viewMode, setViewMode] = useState<ViewMode>('flow')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const fetchFunnels = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await funnelApi.list()
      setFunnels(Array.isArray(response.data) ? response.data : [])
    } catch {
      setError('Failed to load funnels.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFunnels()
  }, [fetchFunnels])

  const filteredFunnels = useFunnelFiltering(funnels, filters)
  const { handleCreate, handleDelete, handleDuplicate } = useFunnelMutations(fetchFunnels)

  const updateFilter = useCallback(<K extends keyof FunnelFilters>(
    key: K,
    value: FunnelFilters[K]
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
  }, [])

  return {
    funnels: filteredFunnels,
    allFunnels: funnels,
    loading,
    error,
    filters,
    viewMode,
    isCreateModalOpen,
    setIsCreateModalOpen,
    setViewMode,
    updateFilter,
    resetFilters,
    fetchFunnels,
    handleCreate,
    handleDelete,
    handleDuplicate,
  }
}
